import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import React from "react";
import { toast } from "react-toastify";
import {
  PrimaryIconButton,
  PrimaryTextButton,
} from "../../../shared/components/Buttons/Buttons";
import PageWidthContainer from "../../../shared/components/PageWidthContainer/PageWidthContainer";
import {
  ComponentHalfSpacing,
  ComponentSpacing,
} from "../../../shared/components/Spacing/Spacing";
import withAppUserData, {
  UserData,
} from "../../../shared/hocs/withAppUserData/withAppUserData";
import { AppNotificationType } from "../../../shared/models/Notification/Notification";
import notificationService from "../../../shared/services/NotificationService/NotificationService";
import pushNotificationsService, {
  ReceivedPushNotificationPayload,
} from "../../../shared/services/PushNotifications/PushNotifications";
import ROUTES from "../Layout/components/Main/components/AppRouter/routes";
import styles from "./styles.module.css";

export type NavProps = {
  contrastText?: boolean;
  hideLoginButton?: boolean;
  forceNotificationsZero?: boolean;
  hideHomeButton?: boolean;
};

function BaseNav({
  contrastText,
  hideLoginButton,
  forceNotificationsZero,
  hideHomeButton,
  appUser,
}: NavProps & UserData) {
  const [notificationsCount, setNotificationsCount] = React.useState<number>(0);

  pushNotificationsService
    .onMessageListener()
    .then((payload) => {
      const pushNotificationPayload =
        payload as ReceivedPushNotificationPayload;

      const emojiMap: { [key in AppNotificationType]: string } = {
        trade_request_sent: "🙏",
        trade_request_received: "👋",
        trade_request_recieved: "👋",
        trade_request_read: "👀",
        trade_request_rejected: "😓",
      };

      toast.info(pushNotificationPayload.notification.body, {
        autoClose: 4000,
        theme: "colored",
        icon: emojiMap[pushNotificationPayload.data.type],
      });

      fetchNotifications();
    })
    .catch((err) => console.log("Unable to process push notification: ", err));

  React.useEffect(fetchNotifications, [appUser]);

  function fetchNotifications() {
    const setData = async () => {
      if (!appUser) return;

      const count = await notificationService.getUnreadNotificationsCount(
        appUser.id
      );

      setNotificationsCount(count);
    };
    setData();
  }

  const MeButton = () => {
    if (hideLoginButton) {
      return null;
    }

    if (appUser)
      return (
        <PrimaryIconButton linkTo={ROUTES.UPDATE_PROFILE}>
          <AccountCircleIcon
            className={
              contrastText
                ? styles["primary-contrast-text"]
                : styles["primary-text"]
            }
          />
        </PrimaryIconButton>
      );

    return (
      <PrimaryTextButton linkTo={ROUTES.SIGN_IN}>
        <span className={contrastText ? styles["primary-contrast-text"] : ""}>
          Entrar
        </span>
      </PrimaryTextButton>
    );
  };

  const NotificationsButton = ({ count }: { count: number }) => {
    if (hideLoginButton) {
      return null;
    }

    const displayedCount = forceNotificationsZero ? 0 : count;

    return (
      <PrimaryIconButton linkTo={ROUTES.NOTIFICATIONS}>
        <Badge badgeContent={displayedCount} color="secondary">
          <NotificationsIcon
            className={
              contrastText
                ? styles["primary-contrast-text"]
                : styles["primary-text"]
            }
          />
        </Badge>
      </PrimaryIconButton>
    );
  };

  const LeftSideComponent = () => {
    if (hideHomeButton) {
      return (
        <div
          className={
            contrastText
              ? styles["primary-contrast-text"]
              : styles["primary-text"]
          }
        >
          <div className={styles.brand}>
            <strong>
              <span>figurinhas</span>
              <span className={styles["brand-color-2"]}>.web</span>
              <span className={styles["brand-color-2"]}>.app</span>
            </strong>
          </div>
        </div>
      );
    }

    return (
      <PrimaryIconButton linkTo={ROUTES.HOME}>
        <HomeIcon
          className={
            contrastText
              ? styles["primary-contrast-text"]
              : styles["primary-text"]
          }
        />
      </PrimaryIconButton>
    );
  };

  return (
    <header className={styles.nav}>
      <PageWidthContainer>
        <div className={styles.container}>
          <LeftSideComponent />

          {!hideHomeButton && (
            <div className={styles["brand-center"]}>
              <ComponentSpacing />
              <strong>
                <span className={styles["primary-text"]}>figurinhas</span>
                <span className={styles["brand-color-2"]}>.web</span>
                <span className={styles["brand-color-2"]}>.app</span>
              </strong>
            </div>
          )}

          <div className="app-horizontal-spacer"></div>

          {appUser !== undefined && appUser?.isComplete && (
            <NotificationsButton count={notificationsCount} />
          )}

          <ComponentHalfSpacing />

          {appUser !== undefined && <MeButton />}
        </div>
      </PageWidthContainer>
    </header>
  );
}

const Nav = withAppUserData<NavProps>(BaseNav);

export default Nav;
